import { createFileRoute, useParams, useNavigate, useSearch } from '@tanstack/react-router'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { getErrorDescription } from '@/lib/error-utils'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { usePublicBooking } from '@/stores/booking.store'
import { usePublicInvoice, useSharePublicInvoiceByEmail } from '@/stores/invoice.store'
import { setToken, getToken } from '@/stores/token.store'
import { getInquiryOrgInfo } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetInquiryOrgInfoRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { usePaymentFlow } from '@/hooks/use-payment-flow'
import { getPaymentSessionContext } from '@/lib/payment-session-context'
import { setPortalContext, setCustomerContext, captureError, trackLegacyPortalBootstrap } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { InvoiceErrorBoundary } from '@/components/error-boundary'
import { InvoiceListItem } from '@/components/invoice-list-item'
import { InvoiceCardWithData } from '@/components/invoice-card-with-data'
import { PayNowBanner } from '@/components/pay-now-banner'
import { PortalHeader } from '@/components/portal-header'
import { formatDateTimeRange } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { formatEventTypeKey } from '@/lib/enum-utils'
import { Calendar, MapPin, Users, Clock, Receipt, Loader2, Send, FileQuestion, ClipboardList } from 'lucide-react'
import { PortalFooter } from '@/components/portal-footer'
import { bookingQuestionnaireRailState } from '@/lib/booking-questionnaire'
import { questionnaireGatesPayment } from '@/lib/questionnaire-gate'
import { BookingQuestionnaireSection } from '@/components/booking-questionnaire-section'
import { validateBookingSearch, resolveBookingSelection, questionnaireReturnInvoiceId } from '@/lib/booking-search'
import { scrollIntoViewWhenStacked } from '@/lib/mobile-layout'

export const Route = createFileRoute('/booking/$id')({
  component: BookingDetailPage,
  validateSearch: validateBookingSearch,
})

function BookingDetailPage() {
  const { id } = useParams({ from: '/booking/$id' })
  const navigate = useNavigate()
  const { session_id: sessionId, panel: panelFromUrl, invoice: invoiceFromUrl } = useSearch({ from: '/booking/$id' })
  const { booking, isLoading, hasLoaded, isError } = usePublicBooking(id)

  const questionnaireRail = bookingQuestionnaireRailState(booking?.questionnaire)
  const showEvent = !!booking?.events && booking.events.length > 0
  const showQuestionnaire = questionnaireRail.visible
  const questionnaireGated = questionnaireGatesPayment(booking)
  // An email pay link's `invoice` param does not get past the questionnaire gate; an invoice the
  // customer clicks in the rail does.
  const [invoicePickedOnPage, setInvoicePickedOnPage] = useState(false)
  // The questionnaire panel replaces the invoice in the URL, so remember which invoice to go
  // back to when the customer saves or leaves it.
  const [invoiceBeforeQuestionnaire, setInvoiceBeforeQuestionnaire] = useState<string | null>(null)

  // Selection is search-param driven and mutually exclusive: exactly one of invoice/event/
  // questionnaire is active. A stale/invalid panel or invoice id falls back to the default
  // (first) invoice, kept out of the URL.
  const selection = useMemo(
    () => resolveBookingSelection({
      panelFromUrl,
      invoiceFromUrl,
      invoiceIds: booking?.invoiceIds,
      showEvent,
      showQuestionnaire,
      questionnaireGated,
      invoicePickedOnPage,
    }),
    [panelFromUrl, invoiceFromUrl, booking, showEvent, showQuestionnaire, questionnaireGated, invoicePickedOnPage],
  )
  const selectedInvoiceId = selection.panel === 'invoice' ? selection.invoiceId : null

  // Shares the connect-query cache with InvoiceCardWithData for the same invoice ID.
  const { invoice: selectedInvoice } = usePublicInvoice(selectedInvoiceId ?? '')

  // Share invoice state
  const [isShareInvoiceDialogOpen, setIsShareInvoiceDialogOpen] = useState(false)
  const [shareInvoiceEmails, setShareInvoiceEmails] = useState('')
  const [shareInvoiceId, setShareInvoiceId] = useState<string | null>(null)
  const { share: shareInvoice, isPending: isSharePending } = useSharePublicInvoiceByEmail()

  const paymentFlow = usePaymentFlow({
    bookingId: id,
  })

  // Set Sentry context when booking data loads
  useEffect(() => {
    if (booking) {
      setPortalContext({
        organizationId: booking.organization?.id,
        invoiceId: selectedInvoiceId || undefined,
      })
      setCustomerContext({
        name: booking.contact?.firstName,
      })
    }
  }, [booking, selectedInvoiceId])

  // Handle checkout session return - when user is redirected back after payment (fallback for ACH)
  useEffect(() => {
    if (sessionId) {
      const context = getPaymentSessionContext(sessionId)
      navigate({
        to: '/booking/$id/processing',
        params: { id },
        search: {
          session_id: sessionId,
          invoice_id: context?.invoiceId,
          schedule_id: context?.scheduleId,
        },
        replace: true,
      })
    }
  }, [sessionId, id, navigate])

  // Keep token extraction for attribution/tracking, but do not require a token.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('token')

    if (urlToken) {
      trackLegacyPortalBootstrap('booking')
      setToken(id, urlToken)

      navigate({
        to: '/booking/$id',
        params: { id },
        search: {},
        replace: true,
      })
    }
  }, [id, navigate])

  // On phones the detail panel sits below the navigator cards, so each selection
  // scrolls it into view — otherwise the tap appears to do nothing.
  const detailPanelRef = useRef<HTMLDivElement>(null)

  // Memoized handlers. panel and invoice are mutually exclusive in the URL, so each selection
  // clears the other.
  const handleInvoiceSelect = useCallback((invoiceId: string) => {
    setInvoicePickedOnPage(true)
    navigate({
      from: '/booking/$id',
      search: (prev) => ({ ...prev, invoice: invoiceId, panel: undefined }),
      replace: true,
    })
    scrollIntoViewWhenStacked(detailPanelRef.current)
  }, [navigate])

  const handleEventSelect = useCallback(() => {
    navigate({
      from: '/booking/$id',
      search: (prev) => ({ ...prev, panel: 'event', invoice: undefined }),
      replace: true,
    })
    scrollIntoViewWhenStacked(detailPanelRef.current)
  }, [navigate])

  const handleQuestionnaireSelect = useCallback(() => {
    if (selectedInvoiceId) setInvoiceBeforeQuestionnaire(selectedInvoiceId)
    navigate({
      from: '/booking/$id',
      search: (prev) => ({ ...prev, panel: 'questionnaire', invoice: undefined }),
      replace: true,
    })
    scrollIntoViewWhenStacked(detailPanelRef.current)
  }, [navigate, selectedInvoiceId])

  // Saving the questionnaire (or backing out of it) moves the customer on to the invoice, the
  // next step on the booking, instead of leaving them on the form they just finished.
  const returnInvoiceId = questionnaireReturnInvoiceId({
    invoiceViewedBefore: invoiceBeforeQuestionnaire,
    invoiceFromUrl,
    invoiceIds: booking?.invoiceIds,
  })
  const handleReturnToInvoice = useCallback(() => {
    if (returnInvoiceId) handleInvoiceSelect(returnInvoiceId)
  }, [returnInvoiceId, handleInvoiceSelect])

  const handlePaySchedule = useCallback((scheduleId: string) => {
    if (selectedInvoiceId) {
      paymentFlow.handlePaySchedule(scheduleId, selectedInvoiceId)
    }
  }, [selectedInvoiceId, paymentFlow])

  const handlePayResidualTip = useCallback(() => {
    if (selectedInvoiceId) {
      paymentFlow.handlePayResidualTip(selectedInvoiceId)
    }
  }, [selectedInvoiceId, paymentFlow])

  const handleVerifyBankAccount = useCallback((verificationUrl: string, scheduleId: string) => {
    if (selectedInvoiceId) {
      paymentFlow.handleVerifyBankAccount(verificationUrl, selectedInvoiceId, scheduleId)
    }
  }, [selectedInvoiceId, paymentFlow])

  const handleShareInvoice = useCallback((invoiceId: string) => {
    setShareInvoiceId(invoiceId)
    setIsShareInvoiceDialogOpen(true)
  }, [])

  const handleShareSubmit = useCallback(async () => {
    if (!shareInvoiceId) return
    const emails = shareInvoiceEmails.split(',').map(e => e.trim()).filter(Boolean)
    if (emails.length === 0) return

    try {
      const result = await shareInvoice(shareInvoiceId, emails)
      toast.success(`Invoice sent to ${result.emailsQueued} recipient${result.emailsQueued !== 1 ? 's' : ''}`)
      setIsShareInvoiceDialogOpen(false)
      setShareInvoiceEmails('')
      setShareInvoiceId(null)
    } catch (error) {
      toast.error('Failed to send invoice. Please try again.', {
        description: getErrorDescription(error),
      })
      console.error('Share invoice error:', error)
      reportError(error, { feature: 'portal_share_invoice', kind: 'mutation' })
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'booking-detail', action: 'share-invoice' })
    }
  }, [shareInvoiceId, shareInvoiceEmails, shareInvoice])

  // Show skeleton while loading or before first load
  if (isLoading || !hasLoaded) {
    return <BookingDetailSkeleton />
  }

  // Show error state
  if (isError || !booking) {
    return <BookingNoAccessState resourceId={id} />
  }

  const questionnaireCard = showQuestionnaire && (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5" />
          Event details
        </CardTitle>
        <CardDescription>
          {questionnaireGated ? 'Required before payment' : 'Help us prepare for your event'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <button
          type="button"
          onClick={handleQuestionnaireSelect}
          className={cn(
            'w-full rounded-lg border p-4 text-left transition-all',
            selection.panel === 'questionnaire'
              ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2'
              : 'hover:border-border hover:bg-elevated',
          )}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold">
                {questionnaireRail.isComplete ? 'View your answers' : 'Answer event questions'}
              </h4>
              <p className="mt-1 text-xs text-muted-foreground">
                {questionnaireRail.isComplete
                  ? `${questionnaireRail.answered} of ${questionnaireRail.total} answered`
                  : `${questionnaireRail.unansweredRequired} required question${questionnaireRail.unansweredRequired === 1 ? '' : 's'} remaining`}
              </p>
            </div>
            <Badge variant={questionnaireRail.isComplete ? 'success' : 'secondary'}>
              {questionnaireRail.isComplete ? 'Done' : 'To do'}
            </Badge>
          </div>
        </button>
      </CardContent>
    </Card>
  )

  const customerName = [booking.contact?.firstName, booking.contact?.lastName].filter(Boolean).join(' ')

  return (
    <div className="min-h-svh flex flex-col bg-background pb-safe-20 lg:pb-0">
      <PortalHeader
        orgName={booking.organization?.name || 'Booking'}
        logoUrl={booking.organization?.logoUrl}
        contactEmail={booking.organization?.contactEmail}
        hasGallery={!!booking.organization?.galleryImages?.length}
        secondary={(
          <div className="flex flex-wrap items-center gap-2">
            {customerName && <span>{customerName}</span>}
			{booking.eventTypeKey && (
              <Badge variant="secondary">
				{booking.eventTypeLabel || formatEventTypeKey(booking.eventTypeKey)}
              </Badge>
            )}
          </div>
        )}
      />

      {/* Main Content - Split Layout */}
      <div className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-6 md:px-8 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:py-8">
        {questionnaireGated && (
          <div className="flex flex-col gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between lg:col-span-2">
            <div className="flex items-start gap-3">
              <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0">
                <h2 className="text-base font-semibold">One step before payment</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {booking.organization?.name || 'This business'} needs a few details about your event before you pay.
                  {' '}
                  {questionnaireRail.unansweredRequired === 1
                    ? '1 required question is'
                    : `${questionnaireRail.unansweredRequired} required questions are`} still unanswered.
                </p>
              </div>
            </div>
            {/* Already-open questionnaire sits beside this on desktop; phones still need the jump. */}
            <Button
              variant="inverse"
              className={cn('min-h-11 shrink-0', selection.panel === 'questionnaire' && 'lg:hidden')}
              onClick={handleQuestionnaireSelect}
            >
              Answer questions
            </Button>
          </div>
        )}

        {/* Left Panel - Invoices & Events */}
        <div className="space-y-6">
          {questionnaireGated && questionnaireCard}
          {/* Invoice List Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoices
              </CardTitle>
              <CardDescription>
                {booking.invoiceIds && booking.invoiceIds.length > 0
                  ? `${booking.invoiceIds.length} invoice(s)`
                  : 'No invoices yet'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {booking.invoiceIds && booking.invoiceIds.length > 0 ? (
                <div className="space-y-2">
                  {booking.invoiceIds.map((invoiceId) => (
                    <InvoiceListItem
                      key={invoiceId}
                      invoiceId={invoiceId}
                      isSelected={selectedInvoiceId === invoiceId}
                      onClick={() => handleInvoiceSelect(invoiceId)}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center text-muted-foreground">
                  <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No invoices yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Events Card */}
          {showEvent && (
            <Card>
              <CardHeader>
                <CardTitle>Events</CardTitle>
                <CardDescription>
                  {booking.events!.length} event(s) scheduled
                </CardDescription>
              </CardHeader>
              <CardContent>
                <button
                  type="button"
                  onClick={handleEventSelect}
                  className={cn(
                    'w-full rounded-lg border p-4 text-left transition-all',
                    selection.panel === 'event'
                      ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2'
                      : 'hover:border-border hover:bg-elevated',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <Calendar className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">View event details</p>
                      {booking.events![0].eventDate && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatDateTimeRange(booking.events![0].eventDate, booking.events![0].eventLength, booking.events![0].eventTimezone)}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              </CardContent>
            </Card>
          )}

          {!questionnaireGated && questionnaireCard}
          </div>

          {/* Right Panel - Selected detail */}
          <div ref={detailPanelRef} className="min-w-0 scroll-mt-4 md:scroll-mt-24 space-y-6 lg:sticky lg:top-24 lg:h-fit">
            {selection.panel === 'questionnaire' ? (
              <BookingQuestionnaireSection
                bookingId={id}
                questionnaire={booking.questionnaire!}
                title="Event details"
                description="Share a few details so we can prepare for your event."
                submitLabel={questionnaireGated
                  ? 'Save and continue to payment'
                  : returnInvoiceId
                    ? 'Save and return to invoice'
                    : questionnaireRail.isComplete ? 'Update answers' : 'Submit answers'}
                onCompleted={handleReturnToInvoice}
                back={returnInvoiceId ? { label: 'Back to invoice', onClick: handleReturnToInvoice } : undefined}
              />
            ) : selection.panel === 'event' ? (
              <Card>
                <CardHeader>
                  <CardTitle>Events</CardTitle>
                  <CardDescription>
                    {booking.events!.length} event(s) scheduled
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {booking.events!.map((event, index) => (
                      <div key={event.id} className="p-4 rounded-lg border">
                        <div className="space-y-3">
                          {/* Event Date & Time */}
                          {event.eventDate && (
                            <div className="flex items-start gap-3">
                              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm font-medium">
                                  {formatDateTimeRange(event.eventDate, event.eventLength, event.eventTimezone)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Event Duration */}
                          {event.eventLength !== undefined && event.eventLength > 0 && (
                            <div className="flex items-start gap-3">
                              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm text-muted-foreground">
                                  {event.eventLength} hour{event.eventLength !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Event Location */}
                          {event.eventLocation && (
                            <div className="flex items-start gap-3">
                              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm text-muted-foreground">{event.eventLocation}</p>
                              </div>
                            </div>
                          )}

                          {/* Guest Count */}
                          {event.eventGuests !== undefined && event.eventGuests > 0 && (
                            <div className="flex items-start gap-3">
                              <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm text-muted-foreground">
                                  {event.eventGuests} guest{event.eventGuests !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Event number indicator for multiple events */}
                        {booking.events!.length > 1 && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="text-xs text-muted-foreground">
                              Event {index + 1} of {booking.events!.length}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : selectedInvoiceId ? (
              <InvoiceErrorBoundary>
                <InvoiceCardWithData
                  invoiceId={selectedInvoiceId}
                  payingScheduleId={paymentFlow.payingScheduleId}
                  optimisticProcessingScheduleId={paymentFlow.optimisticProcessingScheduleId}
                  isVerifyingPayment={paymentFlow.isVerifyingInvoice(selectedInvoiceId)}
                  onPaySchedule={handlePaySchedule}
                  onPayResidualTip={handlePayResidualTip}
                  onVerifyBankAccount={handleVerifyBankAccount}
                  onCancelPayment={paymentFlow.handleCancelPayment}
                  onShareInvoice={handleShareInvoice}
                />
              </InvoiceErrorBoundary>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-muted-foreground">
                    <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>
                      {showQuestionnaire || showEvent
                        ? 'Select an invoice or event details to continue'
                        : 'Select an invoice to view details'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
      </div>

      {/* Share Invoice Dialog */}
      <Dialog open={isShareInvoiceDialogOpen} onOpenChange={setIsShareInvoiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Invoice</DialogTitle>
            <DialogDescription>
              Send the invoice via email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="share-invoice-emails">Send via Email</Label>
              <Textarea
                id="share-invoice-emails"
                placeholder="Enter email addresses, separated by commas"
                value={shareInvoiceEmails}
                onChange={(e) => setShareInvoiceEmails(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={handleShareSubmit}
              disabled={!shareInvoiceEmails.trim() || isSharePending}
            >
              {isSharePending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {isSharePending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer with Terms & Conditions */}
      {booking.organization && (
        <PortalFooter organization={booking.organization} />
      )}

      {/* Sticky mobile pay banner - two taps to checkout without scrolling to find
          the "Pay" button in the payment schedule timeline. Desktop's sticky invoice
          panel already surfaces the same button, so this is lg:hidden. */}
      <PayNowBanner
        invoice={selectedInvoice}
        optimisticProcessingScheduleId={paymentFlow.optimisticProcessingScheduleId}
        onPaySchedule={handlePaySchedule}
        onPayResidualTip={handlePayResidualTip}
        onAnswerQuestionnaire={questionnaireGated ? handleQuestionnaireSelect : undefined}
      />
    </div>
  )
}

function BookingNoAccessState({ resourceId }: { resourceId: string }) {
  const token = getToken(resourceId) || ''
  const request = useMemo(
    () => create(GetInquiryOrgInfoRequestSchema, { token }),
    [token],
  )
  const { data: orgInfo } = useQuery(getInquiryOrgInfo, request, {
    enabled: !!token,
  })

  const orgName = orgInfo?.orgName
  const orgLogoUrl = orgInfo?.orgLogoUrl

  return (
    <div className="min-h-svh bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center space-y-4">
          {orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt={orgName || 'Organization'}
              className="h-20 w-20 mx-auto rounded-lg border border-border object-cover"
            />
          ) : (
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <FileQuestion className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
          {orgName && (
            <div>
              <h1 className="display-heading text-3xl font-semibold tracking-tight text-foreground">
                {orgName}
              </h1>
            </div>
          )}
        </div>

        <Card className="border-amber-300 bg-amber-50/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileQuestion className="h-6 w-6 text-amber-600" />
              <div>
                <CardTitle className="text-amber-800">You don't have access to this booking</CardTitle>
                <CardDescription>We couldn't load the requested document</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {orgName
                ? <>Please reply to your email or contact <strong>{orgName}</strong> and ask them to send you a new link.</>
                : 'Please reply to your email or contact the business that sent it and ask them to send you a new link.'}
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-dim">
          {orgName
            ? `Need help? Contact ${orgName} directly.`
            : 'Need help? Contact the business that sent you this link.'}
        </p>
      </div>
    </div>
  )
}

function BookingDetailSkeleton() {
  return (
    <div className="min-h-svh">
      {/* Header Skeleton */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
          <Skeleton className="h-9 w-36" />
        </div>
      </header>

      {/* Content Skeleton */}
      <div className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-6 md:px-8 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:py-8">
        {/* Left Panel Skeleton */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        </div>

        {/* Right Panel Skeleton */}
        <div>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
